import { useEffect, useState } from 'react';
import { isChatSessionStale } from '@/utils/chatSession';
import { useRecipeById } from '@/lib/recipeService';
import { useCollectionById } from '@/lib/collectionService';
import { useChatMessages } from '@/hooks/useChatMessages';
import { useChatContext } from '@/hooks/useChatContext';
import { getAutoSendMessage } from '@/types/chat';

interface UseAIChatControllerOptions {
  /** Force help mode on (mobile `?from=help` deep link); the widget reads it from context. */
  initialHelpMode?: boolean;
}

/**
 * Shared behaviour for both chat surfaces (desktop widget + mobile page), so
 * they differ only in chrome.
 */
export function useAIChatController(opts: UseAIChatControllerOptions = {}) {
  const {
    currentRecipeId,
    currentCollectionId,
    helpMode,
    setHelpMode,
    pendingAction: contextPendingAction,
    setPendingAction,
  } = useChatContext();

  const recipeId = currentRecipeId ?? undefined;
  const collectionId = currentCollectionId ?? undefined;

  const { recipe: currentRecipe } = useRecipeById(recipeId);
  const { collection: currentCollection } = useCollectionById(collectionId);
  const {
    messages,
    sendMessage,
    resetChat,
    isLoading,
    pendingAction,
    confirmAction,
    cancelAction,
    isConfirming,
  } = useChatMessages({ recipeId, collectionId });

  const [helpModeInitialised] = useState(() => (opts.initialHelpMode ?? false) || helpMode);

  // On mount: enter help mode fresh, or clear a stale session.
  useEffect(() => {
    if (helpModeInitialised) {
      resetChat();
      setHelpMode(true);
    } else if (isChatSessionStale(messages)) {
      resetChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-send a queued substitution. Reacts to the pending action (not just
  // mount) so a second ingredient tap on an already-open chat still fires.
  useEffect(() => {
    const msg = getAutoSendMessage(contextPendingAction);
    if (msg) {
      setPendingAction(null);
      sendMessage(msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextPendingAction]);

  // Scope-change reset lives in ChatContext.openChat, not here — see note there.

  const handleHelp = () => {
    resetChat();
    setHelpMode(true);
  };

  const onSend = (msg: string) => {
    setHelpMode(false);
    sendMessage(msg);
  };

  return {
    recipeId,
    messages,
    isLoading,
    currentRecipe,
    currentCollection,
    pendingAction,
    confirmAction,
    cancelAction,
    isConfirming,
    helpMode,
    resetChat,
    handleHelp,
    onSend,
  };
}
